/**
 * normalizeNextRoute - Safe redirect utility for auth callback
 *
 * Implements CANON v1.0.6 auth-URL policy:
 * - Only internal paths allowed (no external domains)
 * - Strips query params unless explicitly allowed
 * - Defaults to appropriate surface landing page
 *
 * @param nextRoute - Candidate redirect path from onboarding_user RPC
 * @param surface - Current auth surface ('public' | 'admin')
 * @returns Safe, normalized path for client-side redirect
 */

export type AuthSurface = 'public' | 'admin';

const ALLOWED_SURFACES: Record<AuthSurface, string> = {
  public: '/dashboard',
  admin: '/admin#overview'
};

/**
 * Validate and normalize next_route from RPC
 */
export function normalizeNextRoute(
  nextRoute: string | null | undefined,
  surface: AuthSurface = 'public'
): string {
  // Default to surface landing page if no route provided
  if (!nextRoute || nextRoute.trim() === '') {
    return ALLOWED_SURFACES[surface];
  }

  const route = nextRoute.trim();

  // Security: Block external domains
  if (
    route.startsWith('http://') ||
    route.startsWith('https://') ||
    route.startsWith('//')
  ) {
    console.warn(
      `[normalizeNextRoute] Blocked external redirect: ${route}. Using default.`
    );
    return ALLOWED_SURFACES[surface];
  }

  // Ensure path starts with /
  const normalizedPath = route.startsWith('/') ? route : `/${route}`;

  // Strip query params for security (unless explicitly allowed)
  const [pathOnly] = normalizedPath.split('?');

  // Validate path is within allowed surface
  if (surface === 'admin' && !pathOnly.startsWith('/admin')) {
    console.warn(
      `[normalizeNextRoute] Admin surface but path doesn't start with /admin: ${pathOnly}`
    );
    return ALLOWED_SURFACES.admin;
  }

  if (surface === 'public' && pathOnly.startsWith('/admin')) {
    console.warn(
      `[normalizeNextRoute] Public surface but path starts with /admin: ${pathOnly}`
    );
    return ALLOWED_SURFACES.public;
  }

  return pathOnly;
}

/**
 * Determine auth surface from current origin
 */
export function getAuthSurface(origin?: string): AuthSurface {
  const currentOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : '');

  if (currentOrigin.includes('admin')) {
    return 'admin';
  }

  return 'public';
}

/**
 * Build full redirect URL for client-side navigation
 */
export function buildRedirectUrl(
  nextRoute: string | null | undefined,
  surface?: AuthSurface
): string {
  const detectedSurface = surface || getAuthSurface();
  const normalizedPath = normalizeNextRoute(nextRoute, detectedSurface);

  // Client-side: return path only (router handles origin)
  if (typeof window !== 'undefined') {
    return normalizedPath;
  }

  // SSR: would need full URL, but auth callback is client-side only
  return normalizedPath;
}